<template>
  <div class="btb-bar" :class="{ 'btb-bar--empty': !hasJourney }">
    <div class="btb-env">
      <span class="btb-env-label">Environment:</span>
      <span class="btb-env-badge">{{ environment.toUpperCase() }}</span>
    </div>

    <template v-if="hasJourney">
      <div class="btb-stepper">
        <template v-for="(stage, i) in stages">
          <div :key="stage.key" class="btb-step" :class="statusOf(i)">
            <span class="btb-step-dot">
              <svg v-if="statusOf(i) === 'done'" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              <template v-else>{{ i + 1 }}</template>
            </span>
            <span class="btb-step-label">{{ stage.label }}</span>
          </div>
          <div v-if="i < stages.length - 1" :key="stage.key + '-line'" class="btb-step-line" :class="{ done: i < currentIndex }"></div>
        </template>

        <button class="btb-mark-live" :disabled="currentStage === 'live'">Mark live →</button>
      </div>

      <span class="btb-validate-wrap">
        <button class="btb-validate" :class="{ ok: validationResult && validationResult.valid, err: validationResult && !validationResult.valid }" :disabled="validating" title="Validate this journey's structure" @click="$emit('validate')">
          <svg v-if="!validating" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          {{ validating ? 'Validating…' : 'Validate' }}
        </button>
        <span v-if="validationResult" class="btb-validate-msg" :class="{ ok: validationResult.valid, err: !validationResult.valid }">{{ validationResult.message }}</span>
      </span>

      <span class="btb-validate-wrap">
        <button
          class="btb-promote"
          :disabled="!canPromote || promoting"
          :title="canPromote ? 'Promote this journey to the Production environment' : 'Run Validate successfully before promoting'"
          @click="$emit('promote')"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19V5"/><polyline points="5 12 12 5 19 12"/></svg>
          {{ promoting ? 'Saving…' : 'Promote to Production' }}
        </button>
        <span v-if="promoteResult" class="btb-validate-msg" :class="{ ok: promoteResult.success, err: !promoteResult.success }">{{ promoteResult.message }}</span>
      </span>
    </template>

    <span v-else class="btb-empty-msg">No journey loaded</span>
  </div>
</template>

<script>
const STAGES = [
  { key: 'draft', label: 'Draft' },
  { key: 'approved', label: 'Approved' },
  { key: 'live', label: 'Live' }
]

export default {
  name: 'builder-top-bar',
  props: {
    environment: { type: String, default: 'staging' },
    currentStage: { type: String, default: 'approved' },
    hasJourney: { type: Boolean, default: true },
    validating: { type: Boolean, default: false },
    validationResult: { type: Object, default: null },
    promoting: { type: Boolean, default: false },
    promoteResult: { type: Object, default: null }
  },
  data () {
    return { stages: STAGES }
  },
  computed: {
    currentIndex () {
      const i = this.stages.findIndex(s => s.key === this.currentStage)
      return i === -1 ? 0 : i
    },
    canPromote () {
      return !!(this.validationResult && this.validationResult.valid)
    }
  },
  methods: {
    statusOf (i) {
      if (i < this.currentIndex) return 'done'
      if (i === this.currentIndex) return 'current'
      return 'pending'
    }
  }
}
</script>

<style scoped>
.btb-bar{
  position:fixed;top:0;left:var(--sidebar-w, 40px);right:var(--explorer-w, 40px);z-index:950;
  height:48px;background:#0b1f3a;color:#fff;
  display:flex;align-items:center;gap:24px;padding:0 20px;
  font-family:'Inter',system-ui,sans-serif;font-size:13px;
  transition:left .15s ease,right .15s ease;
}
.btb-bar--empty{opacity:0.55}
.btb-empty-msg{font-size:12.5px;color:rgba(255,255,255,0.45);font-style:italic}

.btb-env{display:flex;align-items:center;gap:8px;flex-shrink:0}
.btb-env-label{color:rgba(255,255,255,0.6);font-size:12.5px}
.btb-env-badge{
  background:rgba(245,166,35,0.18);color:#f5a623;border:1px solid rgba(245,166,35,0.35);
  font-size:10.5px;font-weight:700;letter-spacing:0.04em;padding:3px 9px;border-radius:999px;
}

.btb-stepper{display:flex;align-items:center;gap:8px;flex:1}
.btb-step{display:flex;align-items:center;gap:7px;flex-shrink:0}
.btb-step-dot{
  width:20px;height:20px;border-radius:50%;display:flex;align-items:center;justify-content:center;
  font-size:10.5px;font-weight:700;flex-shrink:0;border:1.5px solid rgba(255,255,255,0.3);color:rgba(255,255,255,0.55);
}
.btb-step-label{font-size:12.5px;color:rgba(255,255,255,0.55)}
.btb-step.done .btb-step-dot{background:#14b8a6;border-color:#14b8a6;color:#0b1f3a}
.btb-step.done .btb-step-label{color:rgba(255,255,255,0.75)}
.btb-step.current .btb-step-dot{background:#fff;border-color:#fff;color:#0b1f3a}
.btb-step.current .btb-step-label{color:#fff;font-weight:600}
.btb-step-line{width:28px;height:1.5px;background:rgba(255,255,255,0.25);flex-shrink:0}
.btb-step-line.done{background:#14b8a6}

.btb-mark-live{
  margin-left:8px;padding:6px 14px;border-radius:7px;border:none;background:#fff;color:#0b1f3a;
  font-size:12.5px;font-weight:600;cursor:pointer;font-family:inherit;white-space:nowrap;
}
.btb-mark-live:hover:not(:disabled){background:#f0f0f0}
.btb-mark-live:disabled{opacity:0.4;cursor:default}

.btb-validate-wrap{display:flex;align-items:center;gap:8px;flex-shrink:0}
.btb-validate{
  flex-shrink:0;display:flex;align-items:center;gap:6px;padding:7px 14px;border-radius:7px;
  border:1px solid rgba(255,255,255,0.15);background:rgba(255,255,255,0.06);color:#fff;
  font-size:12.5px;font-weight:600;cursor:pointer;font-family:inherit;white-space:nowrap;
}
.btb-validate:hover:not(:disabled){background:rgba(255,255,255,0.12)}
.btb-validate:disabled{opacity:0.5;cursor:default}
.btb-validate.ok{border-color:rgba(20,184,166,0.5);color:#14b8a6}
.btb-validate.err{border-color:rgba(239,68,68,0.5);color:#ef4444}
.btb-validate-msg{font-size:12px;max-width:260px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.btb-validate-msg.ok{color:#14b8a6}
.btb-validate-msg.err{color:#ef4444}

.btb-promote{
  flex-shrink:0;display:flex;align-items:center;gap:6px;padding:7px 14px;border-radius:7px;
  border:1px solid rgba(255,255,255,0.15);background:rgba(255,255,255,0.06);color:rgba(255,255,255,0.4);
  font-size:12.5px;font-weight:600;cursor:not-allowed;font-family:inherit;white-space:nowrap;
}
.btb-promote:not(:disabled){
  color:#fff;background:rgba(255,255,255,0.12);cursor:pointer;
}
.btb-promote:not(:disabled):hover{background:rgba(255,255,255,0.18)}
</style>



// WEBPACK FOOTER //
// BuilderTopBar.vue?008e6e5e